import React, { Suspense } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import "./App.scss";
import SuspenseLoader from "./Components/Loaders/SuspenseLoader";
import PrivateRoute from "./Routes/PrivateRoute";
import PublicRoute from "./Routes/PublicRoute";
import PrivateSellerRoute from "./Routes/PrivateSellerRoute";
import PrivateLogisticRoute from "./Routes/PrivateLogisticRoute";
import AccessDenied from "./Containers/NoMatch/AccessDenied";
import DwollaAddOwner from "./Components/Dwolla/Dwolla-add-owner";
import OrdrReceipt from "./Components/common/OrdrReceipt/OrdrReceipt";
import PrivateAdminRoute from "./Routes/PrivateAdminRoute";
import { ROLE_LOGISTIC, ROLE_COMMON_CARRIER, ROLE_SELLER } from "./Config/config";
import { getLocalStorageItem } from "./Helper/helper";

const RoleBasedRedirect = () => {
	const userDetails = getLocalStorageItem("ORDER_USER_DETAILS");
	const role = userDetails?.role || localStorage.getItem("ORDER_USER_ROLE");
	if (role == ROLE_LOGISTIC || role == ROLE_COMMON_CARRIER || role == 4 || role == 6) {
		return <Navigate to="/open-orders" replace />;
	} else if (role == 5 || role == "5") {
		return <Navigate to="/admin-dashboard" replace />;
	} else {
		return <Navigate to="/bids" replace />;
	}
};

const HomePage = React.lazy(() => import("./Containers/HomePage/HomePage"));

const Dashboard = React.lazy(() => import("./Containers/Dashboard/Dashboard"));
const OpenOrdersSeller = React.lazy(() => import("./Components/Seller/OpenOrders/OpenOrders"));
const PrevOrdersSeller = React.lazy(() => import("./Components/Seller/PrevOrders/PrevOrders"));
const Bids = React.lazy(() => import("./Components/Seller/Bids/Bids"));

const ProfileSection = React.lazy(() => import("./Components/ProfileSection/ProfileSection"));

const OpenOrderBidrequest = React.lazy(() => import("./Components/common/Requests/OpenOrderBidrequest"));
const PrevOrderBidrequest = React.lazy(() => import("./Components/common/Requests/PrevOrderBidrequest"));

const BidRequest = React.lazy(() => import("./Components/common/Requests/BidRequest"));

const Funds = React.lazy(() => import("./Containers/Funds/Funds"));
const RatingsFromBuyer = React.lazy(() => import("./Components/common/RatingsFromBuyer/RatingsFromBuyer"));
const RatingsToLogistics = React.lazy(() => import("./Components/common/RatingsToLogistics/RatingsToLogistics"));

// Logistic Members Routes

const OpenOrdersLogisticMember = React.lazy(() => import("./Components/LogisticMember/OpenOrders/OpenOrders"));
const OrdersInProgressLogisticMember = React.lazy(() => import("./Components/LogisticMember/OrdersInProgress/OrdersInProgress"));
const OrderInProgressDetails = React.lazy(() => import("./Components/LogisticMember/OrdersInProgress/OrderInProgressDetails"));
const Drivers = React.lazy(() => import("./Components/LogisticMember/Drivers/Drivers"));
const AddDriver = React.lazy(() => import("./Components/LogisticMember/Drivers/AddDriver"));
const EditDriver = React.lazy(() => import("./Components/LogisticMember/Drivers/EditDriver"));
const AssignDriver = React.lazy(() => import("./Components/LogisticMember/Drivers/AssignDriver"));
const OpenOrderDetails = React.lazy(() => import("./Components/LogisticMember/OpenOrders/OpenOrderDetails"));
const BOL = React.lazy(() => import("./Components/LogisticMember/BOL"));
const TrackOrder = React.lazy(() => import("./Components/LogisticMember/OrdersInProgress/TrackOrder"));

const SignIn = React.lazy(() => import("./Components/Auth/Signin"));
const SignUp = React.lazy(() => import("./Components/Auth/Signup"));
const OtpVerification = React.lazy(() => import("./Components/common/OtpFlow/OtpVerification/OtpVerification"));
const VerifyOTP = React.lazy(() => import("./Components/Auth/VerifyOTP"));
const VerifyEmailOTP = React.lazy(() => import("./Components/Auth/VerifyEmailOtp"));
const ConnectCompany = React.lazy(() => import("./Components/Auth/ConnectCompany"));
const Terms = React.lazy(() => import("./Components/Auth/Terms"));
const PaymentInfo = React.lazy(() => import("./Components/Auth/PaymentInfo"));
const AddEditLocation = React.lazy(() => import("./Components/Auth/AddEditLocation"));
const LocationProduct = React.lazy(() => import("./Components/Auth/LocationProduct"));
const LocationNote = React.lazy(() => import("./Components/Auth/LocationNote"));
const ReviewDetails = React.lazy(() => import("./Components/Auth/ReviewDetails"));
const DefaultComponent = React.lazy(() => import("./Components/Default"));
const ForgetPassword = React.lazy(() => import("./Components/Auth/ForgetPassword"));
const Notifications = React.lazy(() => import("./Components/NotifyOrders/Notifications"));

const AccountSettingsSection = React.lazy(() => import("./Components/AccountSettingsSection/AccountSettingsSection"));
const SignUpForLogistic = React.lazy(() => import("./Components/Auth/SignupFroLogistic"));
const Invite = React.lazy(() => import("./Components/Invite/Invite"));
const InvitedUsers = React.lazy(() => import("./Components/Invite/InvitedUsers"));
const NotFound = React.lazy(() => import("./Containers/NoMatch/NotFound"));
const Chat = React.lazy(() => import("./Components/Chat/Chat"));
const TermsAndPolicy = React.lazy(() => import("./Components/TermsAndPolicy/TermsAndPolicy"));
const GetHelp = React.lazy(() => import("./Components/Help/GetHelp"));
const Feedback = React.lazy(() => import("./Components/Help/Feedback"));
const AutoPricing = React.lazy(() => import("./Components/AutoPricing"));
const FreightCharges = React.lazy(() => import("./Components/FreightCharges"));
const AddFreightCharges = React.lazy(() => import("./Components/AddFreightCharges"));
const AddEditLocationNew = React.lazy(() => import("./Components/Auth/AddEditLocationNew"));
// const OwnerDetails = React.lazy(() => import("./Components/Auth/OwnerDetails"));
const DwollaComponent = React.lazy(() => import("./Components/Dwolla/Dwolla"));
const DwollaAddBankAccountComponent = React.lazy(() => import("./Components/Dwolla/DwollaAddBankAccount"));
const DwollaBalanceComponent = React.lazy(() => import("./Components/Dwolla/Dwolla-balance"));
const DwollaPayRechargeWalletComponent = React.lazy(() => import("./Components/Dwolla/Dwolla-recharge-wallet"));
const DwollaDocumentsComponent = React.lazy(() => import("./Components/Dwolla/Dwolla-document"));

const OwnerDetails = React.lazy(() => import("./Components/OwnerDetails"));
const DwollaAddBankAccViaMicroTxns = React.lazy(() => import("./Components/Dwolla/Dwolla-add-bank-account-microtxns"));
const ControllerDetails = React.lazy(() => import("./Components/ControllerDetails"));
const ManageFundingMethods = React.lazy(() => import("./Components/ManageFundingMethods"));
const MoneyTransfer = React.lazy(() => import("./Components/MoneyTransfer"));
const Transactions = React.lazy(() => import("./Components/Transactions"));
const CompanyDetails = React.lazy(() => import("./Components/CompanyDetails"));

const Terminals = React.lazy(() => import("./Components/Seller/Terminals/Terminals"));
const EditTerminal = React.lazy(() => import("./Components/Seller/Terminals/TerminalsEdit/TerminalsEdit"));
const AddTerminal = React.lazy(() => import("./Components/Seller/Terminals/TerminalsAdd/TerminalsAdd"));

const OpisPrices = React.lazy(() => import("./Components/Seller/ClosedMarket/OpisPrices"));
const SetMargins = React.lazy(() => import("./Components/Seller/ClosedMarket/SetMargins"));
const QuotationManager = React.lazy(() => import("./Components/Seller/ClosedMarket/QuotationManager"));
const ClosedMarketOpenOrders = React.lazy(() => import("./Components/Seller/ClosedMarket/ClosedMarketOpenOrders"));
const ClosedMarketOrderDetail = React.lazy(() => import("./Components/Seller/ClosedMarket/ClosedMarketOrderDetail"));
const HistoricalOpis = React.lazy(() => import("./Components/Seller/ClosedMarket/HistoricalOpis"));
const HistoricalQuotations = React.lazy(() => import("./Components/Seller/ClosedMarket/HistoricalQuotations"));
const OperationalSettings = React.lazy(() => import("./Components/Seller/OperationalSettings/OperationalSettings"));
const Plans = React.lazy(() => import("./Components/Seller/Plans/Plans"));
const PlansIntro = React.lazy(() => import("./Components/Seller/Plans/PlansIntro/PlansIntro"));
const LogisticsPastOrders = React.lazy(() => import("./Components/LogisticMember/PastOrders/PastOrders"));
const ClosedOrderDetails = React.lazy(() => import("./Components/LogisticMember/PastOrders/ClosedOrderDetails"));
const AdminDashboard = React.lazy(() => import("./Components/Admin/AdminDashboard"));
const ReferredBuyers = React.lazy(() => import("./Components/Admin/ReferredBuyers"));
const ClosedMarketsAdmin = React.lazy(() => import("./Components/Admin/ClosedMarketsAdmin"));
const ConnectCompanyCarrier = React.lazy(() => import("./Components/Auth/ConnectCompanyCarrier"));
const CarrierFreightCharges = React.lazy(() => import("./Components/FreightCharges/CarrierFreightCharges"));
const AddCarrierFreightCharges = React.lazy(() => import("./Components/FreightCharges/AddCarrierFreightCharges"));

// Create the function
export function AddLibrary(urlOfTheLibrary) {
	const script = document.createElement("script");
	script.src = urlOfTheLibrary;
	script.async = true;
	document.body.appendChild(script);
}

const App = () => {
	const loader = useSelector((state) => state.userStore.loader);

	return (
		<>
			<Suspense fallback={<SuspenseLoader />}>
				<div className="App">
					{/* Call the function to add a library */}
					{AddLibrary("https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js")}
				</div>
				{/* This is to get the pre-data for some of the routes */}
				{loader ? (
					<div className="sweet-loading">
						<Oval height={80} width={80} color="#e0faa2" secondaryColor="#e0faa2" />
					</div>
				) : null}

				<DefaultComponent />

				<Routes>
					<Route path="location/add" element={<AddEditLocationNew />} />
					<Route path="invoice" element={<OrdrReceipt />} />



					<Route path="/" element={<HomePage />} />

					{/* <Route path="plans" element={<Plans />} /> */}



					{/* Public Routes */}
					<Route
						path="/"
						element={
							<PublicRoute>
								<Outlet />
							</PublicRoute>
						}
					>
						{/* Current Flow ----------------------- */}
						<Route path="login" element={<SignIn />} />
						<Route path="signup" element={<SignUp />} />
						<Route path="signup-logistic" element={<SignUpForLogistic />} />
						<Route path="verify-otp" element={<VerifyOTP />} />
						<Route path="verify-email-otp" element={<VerifyEmailOTP />} />
						<Route path="connect-company" element={<ConnectCompany />} />
						<Route path="payment-info" element={<PaymentInfo />} />
						<Route path="location/add" element={<AddEditLocationNew />} />
						<Route path="location/edit/:id" element={<AddEditLocationNew />} />
						<Route path="location-product" element={<LocationProduct />} />
						<Route path="location-note" element={<LocationNote />} />
						<Route path="review-details" element={<ReviewDetails />} />
						<Route path="plans" element={<Plans />} />
						<Route path="plans/introduction" element={<PlansIntro />} />
						{/* --------------------------- */}

						{/* <Route path="terms-of-services" element={<Terms />} />
						<Route path="owner-details" element={<OwnerDetails />} />
						<Route path="contractor-details" element={<OwnerDetails />} />
						<Route path="location/new" element={<AddEditLocationNew />} />
						<Route path="location/new/:id" element={<AddEditLocationNew />} /> */}

						<Route path="forgot-password" element={<ForgetPassword />} />

						<Route path="terms-of-service" element={<TermsAndPolicy />} />
						<Route path="privacy-policy" element={<TermsAndPolicy />} />
						<Route path="carrier/connect-company" element={<ConnectCompanyCarrier />} />
					</Route>

					{/* Private/Protected Routes */}

					<Route
						path="/"
						element={
							<PrivateRoute>
								<Dashboard />
							</PrivateRoute>
						}
					>
						<Route index element={<RoleBasedRedirect />} />

						{/* Seller Routes */}
						<Route
							path="/"
							element={
								<PrivateSellerRoute>
									<Outlet />
								</PrivateSellerRoute>
							}
						>
							<Route path="bids" element={<Bids />}>
								<Route path=":orderId" element={<BidRequest />} />
							</Route>
							<Route path="openorders" element={<OpenOrdersSeller />}>
								<Route path=":orderId" element={<OpenOrderBidrequest />} >
								</Route>
								<Route path=":orderId/track" element={<TrackOrder />} />

							</Route>
							<Route path="prevorders" element={<PrevOrdersSeller />}>
								<Route path=":orderId" element={<PrevOrderBidrequest />} />
								<Route path=":orderId/track" element={<TrackOrder />} />
								<Route path=":orderId/feedback" element={<RatingsFromBuyer />} />
								<Route path=":orderId/review" element={<RatingsToLogistics />} />
							</Route>
							<Route path="auto-pricing" element={<AutoPricing />} />
							<Route path="closed-market/opis-prices" element={<OpisPrices />} />
							<Route path="closed-market/set-margins" element={<SetMargins />} />
							<Route path="closed-market/quotations" element={<QuotationManager />} />
							<Route path="closed-market/open-orders" element={<ClosedMarketOpenOrders />}>
								<Route path=":orderId" element={<ClosedMarketOrderDetail />} />
								<Route path=":orderId/track" element={<TrackOrder />} />
							</Route>
							<Route path="closed-market/historical-opis" element={<HistoricalOpis />} />
							<Route path="closed-market/historical-quotations" element={<HistoricalQuotations />}>
								<Route path=":orderId" element={<ClosedMarketOrderDetail />} />
								<Route path=":orderId/track" element={<TrackOrder />} />
							</Route>
							<Route path="freight-rates" element={<FreightCharges />} />
							<Route path="freight-rates/add" element={<AddFreightCharges />} />
							<Route path="/dwolla" element={<DwollaComponent />} />
							<Route path="/add-bank-account" element={<DwollaAddBankAccountComponent />} />
							<Route path="/add-bank-account-dwolla" element={<DwollaAddBankAccViaMicroTxns />} />
							<Route path="/dwolla-balance" element={<DwollaBalanceComponent />} />
							<Route path="/dwolla-document/:userId" element={<DwollaDocumentsComponent />} />
							<Route path="/dwolla-recharge-wallet" element={<DwollaPayRechargeWalletComponent />} />
							<Route path="/dwolla-add-owner" element={<DwollaAddOwner />} />
							<Route path="terminals" element={<Terminals />} />
							<Route path="terminals/addTerminal" element={<AddTerminal />} />
							<Route path="terminals/editTerminal/:terminalId" element={<EditTerminal />} />
							<Route path="op-settings" element={<OperationalSettings />} />

						</Route>


						{/* Logistic Manager Routes */}
						<Route
							path="/"
							element={
								<PrivateLogisticRoute>
									<Outlet />
								</PrivateLogisticRoute>
							}
						>
							<Route path="open-orders" element={<OpenOrdersLogisticMember />} />
							<Route path="open-orders/:orderId" element={<OpenOrderDetails />} />
							<Route path="orders-in-progress" element={<OrdersInProgressLogisticMember />} />
							{/* <Route path="past-orders" element={<LogisticsPastOrders />} /> */}
							{/* <Route path="past-orders" element={<PrevOrdersSeller />} /> */}

							<Route path="closed-orders" element={<LogisticsPastOrders />} />
							<Route path="closed-orders/:orderId" element={<ClosedOrderDetails />} />
							<Route path="closed-orders/track/:orderId" element={<TrackOrder />} />
							<Route path="orders-in-progress/track/:orderId" element={<TrackOrder />} />
							<Route path="orders-in-progress/:orderId" element={<OrderInProgressDetails />} />
							<Route path="drivers" element={<Drivers />} />
							<Route path="drivers/add" element={<AddDriver />} />
							<Route path="assign-driver/:id" element={<AssignDriver />} />
							<Route path="drivers/edit/:driverId" element={<EditDriver />} />
							<Route path="orders-in-progress/bol/:orderId" element={<BOL />} />
							<Route path="carrier-freight-rates" element={<CarrierFreightCharges />} />
							<Route path="carrier-freight-rates/add" element={<AddCarrierFreightCharges />} />
						</Route>

						{/* Common Routes */}
						<Route path="chat/:id" element={<Chat />} />
						<Route path="notifications" element={<Notifications />} />
						<Route path="profile" element={<ProfileSection />} />
						<Route path="account-settings" element={<AccountSettingsSection />} />
						<Route path="invite" element={<Invite />} />
						<Route path="manage-invitations" element={<InvitedUsers />} />
						<Route path="get-help" element={<GetHelp />} />
						<Route path="feedback" element={<Feedback />} />
						<Route path="manage-users" element={<InvitedUsers />} />
						<Route path="owner-details" element={<OwnerDetails />} />
						<Route path="controller-details" element={<ControllerDetails />} />
						<Route path="manage-funding-methods" element={<ManageFundingMethods />} />
						<Route path="money-transfer" element={<MoneyTransfer />} />
						<Route path="transactions" element={<Transactions />} />
						<Route path="company" element={<CompanyDetails />} />

						{/* Admin Routes */}
						<Route
							path="/"
							element={
								<PrivateAdminRoute>
									<Outlet />
								</PrivateAdminRoute>
							}
						>
							<Route path="admin-dashboard" element={<AdminDashboard />} />
							<Route path="admin/referred-buyers" element={<ReferredBuyers />} />
							<Route path="admin/closed-markets" element={<ClosedMarketsAdmin />} />
						</Route>


					</Route>

					<Route path="managefunds" element={<Funds />} />
					<Route path="404" element={<NotFound />} />
					<Route path="access-denied" element={<AccessDenied />} />
					<Route path="*" element={<NotFound />} />
				</Routes>
			</Suspense>
			<Toaster />
		</>
	);
};

export default App;
